// derivePipelineStage — Section 6 (spec 2) / Section 14 (spec 1).
//
// Insurance-specific pipeline stages DERIVED from existing lead
// signals. Chosen "Option B" — no CHECK enum migration, backwards
// compatible, but reads richer than the raw 5-stage enum.
//
// Stages (in order):
//   lead_received      — status='new', no touches yet
//   ai_qualified       — status='new' OR 'contacted' AND >=1 outreach
//   discovery          — status='contacted' AND notes captured
//   needs_quote        — has phone + email + vertical, no policy yet
//   waiting_spouse     — notes mentions "spouse/wife/husband/consult"
//   medical_review     — vertical=health AND notes mentions condition
//   carrier_match      — has insurance_profile with carrier fields
//   app_started        — status='application'
//   pending_signature  — status='application' AND has trustedFormCert
//   issued             — status='sold' AND has convertedClientId
//   retention          — status='sold' AND last touch > 60d
//   referral_request   — status='sold' AND no rewardSent flag
//
// Returns { key, label } — the frontend can map key → color / order.

const STAGE_META = [
  { key: 'lead_received',     label: 'Lead Received' },
  { key: 'ai_qualified',      label: 'AI Qualified' },
  { key: 'discovery',         label: 'Discovery' },
  { key: 'needs_quote',       label: 'Needs Quote' },
  { key: 'waiting_spouse',    label: 'Waiting on Spouse' },
  { key: 'medical_review',    label: 'Medical Review' },
  { key: 'carrier_match',     label: 'Carrier Match' },
  { key: 'app_started',       label: 'Application Started' },
  { key: 'pending_signature', label: 'Pending Signature' },
  { key: 'issued',            label: 'Issued' },
  { key: 'retention',         label: 'Retention' },
  { key: 'referral_request',  label: 'Referral Request' },
];

const DAY_MS = 86_400_000;

export function derivePipelineStage(lead, callHistory = [], outreachLog = []) {
  const notes = String(lead?.notes || '').toLowerCase();
  const status = String(lead?.status || 'new').toLowerCase();
  const now = Date.now();
  const touches = callHistory.length + outreachLog.length;

  if (status === 'sold' || status === 'closed') {
    if (lead?.rewardSent === false || (lead?.rewardRequested && !lead?.rewardSent)) {
      return { key: 'referral_request', label: 'Referral Request' };
    }
    const lastTouch = [...callHistory, ...outreachLog].sort((a, b) =>
      String(b.at || b.created_at || '').localeCompare(String(a.at || a.created_at || ''))
    )[0];
    if (lastTouch) {
      const daysSince = (now - Date.parse(lastTouch.at || lastTouch.created_at || 0)) / DAY_MS;
      if (daysSince > 60) return { key: 'retention', label: 'Retention' };
    }
    return { key: 'issued', label: 'Issued' };
  }

  if (status === 'application') {
    if (lead?.trustedFormCert || lead?.esignRequested) {
      return { key: 'pending_signature', label: 'Pending Signature' };
    }
    return { key: 'app_started', label: 'Application Started' };
  }

  const p = lead?.insurance_profile || {};
  const hasCarrier = p.health?.current_carrier || p.medicare?.current_carrier || p.life?.existing_policies;
  if (hasCarrier) return { key: 'carrier_match', label: 'Carrier Match' };

  if (String(lead?.vertical || 'health').toLowerCase() === 'health') {
    const conditions = p.health?.conditions || '';
    if (conditions || /diabetes|hypertension|cancer|heart|copd|kidney/.test(notes)) {
      return { key: 'medical_review', label: 'Medical Review' };
    }
  }

  if (/(spouse|wife|husband|consult|check with)/.test(notes)) {
    return { key: 'waiting_spouse', label: 'Waiting on Spouse' };
  }

  const hasQuoteReady = lead?.phone && lead?.email && lead?.vertical;
  if (hasQuoteReady && !p.health?.primary_doctor && status === 'contacted') {
    return { key: 'needs_quote', label: 'Needs Quote' };
  }

  if (status === 'contacted' && notes.length > 20) {
    return { key: 'discovery', label: 'Discovery' };
  }

  if (touches > 0) return { key: 'ai_qualified', label: 'AI Qualified' };
  return { key: 'lead_received', label: 'Lead Received' };
}

export const PIPELINE_STAGES_ORDERED = STAGE_META;

export function stageColor(key) {
  const c = {
    lead_received:     '#94a3b8',
    ai_qualified:      '#0891b2',
    discovery:         '#22d3ee',
    needs_quote:       '#facc15',
    waiting_spouse:    '#f97316',
    medical_review:    '#f43f5e',
    carrier_match:     '#a855f7',
    app_started:       '#8b5cf6',
    pending_signature: '#7c3aed',
    issued:            '#22c55e',
    retention:         '#059669',
    referral_request:  '#ec4899',
  };
  return c[key] || '#64748b';
}
